import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
//#region types
import { CurrencyEnums } from "models/enums";
import { Amount } from "models/utilTypes/currencyType";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelWintSalaryEarlyPayout as EarlyPayoutTypes } from "wint-api-client/dist/types";
//#endregion
//#region components
import { TableAsync, Tags } from "components/common";
import { TableSkeleton } from "components/common/skeletons";
import useEarlyPayoutService from "serviceHooks/useEarlyPayoutService";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const { PaymentStatus, EarlyPayoutKind } = EarlyPayoutTypes;
type EarlyPayoutItem = EarlyPayoutTypes.EarlyPayoutItem;

interface EarlyPayoutListProps {
  personId: number;
  yearMonth: number;
}

const getStatusTagClass = (status: EarlyPayoutTypes.PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.Verified:
      return cssExports.greenTag;
    case PaymentStatus.New:
    case PaymentStatus.ReceivedByPaymentService:
    case PaymentStatus.WaitingToBeSigned:
    case PaymentStatus.WaitingToBeSent:
    case PaymentStatus.Sent:
    case PaymentStatus.Pending:
      return cssExports.blueTag;
    case PaymentStatus.RejectedByPaymentService:
    case PaymentStatus.Stopped:
    case PaymentStatus.Rejected:
    case PaymentStatus.PayoutDateTooCloseInTime:
    case PaymentStatus.Error:
      return cssExports.redTag;
    case PaymentStatus.Removed:
      return cssExports.grayTag;
    default:
      return cssExports.grayTag;
  }
};

const getStatusTranslationKey = (status: EarlyPayoutTypes.PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.New:
      return TranslationKeys.Salary.ExtraPayoutListStatusNew;
    case PaymentStatus.RejectedByPaymentService:
      return TranslationKeys.Salary.ExtraPayoutListStatusRejectedByPaymentService;
    case PaymentStatus.ReceivedByPaymentService:
      return TranslationKeys.Salary.ExtraPayoutListStatusReceivedByPaymentService;
    case PaymentStatus.WaitingToBeSigned:
      return TranslationKeys.Salary.ExtraPayoutListStatusWaitingToBeSigned;
    case PaymentStatus.WaitingToBeSent:
      return TranslationKeys.Salary.ExtraPayoutListStatusWaitingToBeSent;
    case PaymentStatus.Sent:
      return TranslationKeys.Salary.ExtraPayoutListStatusSent;
    case PaymentStatus.Verified:
      return TranslationKeys.Salary.ExtraPayoutListStatusVerified;
    case PaymentStatus.Stopped:
      return TranslationKeys.Salary.ExtraPayoutListStatusStopped;
    case PaymentStatus.Rejected:
      return TranslationKeys.Salary.ExtraPayoutListStatusRejected;
    case PaymentStatus.Pending:
      return TranslationKeys.Salary.ExtraPayoutListStatusPending;
    case PaymentStatus.Removed:
      return TranslationKeys.Salary.ExtraPayoutListStatusRemoved;
    case PaymentStatus.PayoutDateTooCloseInTime:
      return TranslationKeys.Salary.ExtraPayoutListStatusPayoutDateTooCloseInTime;
    case PaymentStatus.Error:
      return TranslationKeys.Salary.ExtraPayoutListStatusError;
    default:
      return TranslationKeys.Salary.ExtraPayoutListStatusUnknown;
  }
};

const EarlyPayoutList: React.FC<EarlyPayoutListProps> = ({ personId, yearMonth }) => {
  const { getPayouts } = useEarlyPayoutService();
  const [payouts, setPayouts] = useState<EarlyPayoutItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const result = await getPayouts(personId, yearMonth);
        setPayouts(result);
      } catch {
        // Error display handled by API client (showErrors: true)
      } finally {
        setLoading(false);
      }
    };

    fetchPayouts();
  }, [personId, yearMonth]);

  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <TableAsync<EarlyPayoutItem>
      filter={payouts}
      pagination={false}
      properties={{
        payoutDate: {
          description: TranslationKeys.Common.Date,
          formatter: (item: EarlyPayoutItem) =>
            LocalizationHelperInstance.getFormattedDate(new Date(item.payoutDate), {
              day: "numeric",
              month: "long",
            }),
          disableSort: true,
        },
        kind: {
          description: TranslationKeys.Common.Type,
          formatter: (item: EarlyPayoutItem) => {
            switch (item.kind) {
              case EarlyPayoutKind.ExtraSalary:
                return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPayoutTypeExtra);
              case EarlyPayoutKind.SalaryAdvance:
                return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutPayoutTypeAdvance);
              default:
                // No recorded kind (legacy payout), or an unknown/future value.
                return "—";
            }
          },
          disableSort: true,
        },
        amount: {
          description: TranslationKeys.Common.Amount,
          formatter: (item: EarlyPayoutItem) => new Amount(CurrencyEnums.CurrencyCode.SEK, item.amount),
          alignment: "right",
          disableSort: true,
        },
        paymentState: {
          description: TranslationKeys.Common.Status,
          formatter: (item: EarlyPayoutItem) => (
            <Grid container justifyContent="flex-end">
              <Tags
                tags={[LocalizationHelperInstance.getFormattedMessage(getStatusTranslationKey(item.paymentState))]}
                className={getStatusTagClass(item.paymentState)}
              />
            </Grid>
          ),
          alignment: "right",
          disableSort: true,
          noWrap: true,
        },
      }}
    />
  );
};

export default EarlyPayoutList;
